import type { ImageLabel, ToolImage, ToolState } from '../types/LabelingTypes';
import {
  beginVerification,
  drawVerificationReplacement,
  expectedLabel,
} from './dichotomousVerification';

/**
 * Pure, framework-free binary-search state machine for dichotomous labeling.
 *
 * Steps 1 (endpoints), 2 (narrowing) and the answering side of step 3 (verification) of the spec
 * live here; the sampling and zone-expectation rules of step 3 and the bulk plan are owned by
 * sibling modules (`dichotomousVerification.ts`, `dichotomousPlan.ts`) — this file must stay free
 * of React, storage and API imports.
 *
 * Unreadable images: the search only ever reasons about images that can be judged. An image the
 * annotator cannot read is *moved out* of `keys` into `unreadable` and every index is shifted
 * accordingly, so the bracket, the answer map and every invariant below keep describing a plain
 * OK → Worn sequence. Images already labeled unreadable are left out from the start.
 */

// ---------- types ----------

export type DichotomousPhase =
  | 'endpoint-lo'
  | 'endpoint-hi'
  | 'search'
  | 'verify'
  | 'conflict'
  | 'summary'
  | 'all-worn'
  | 'all-ok'
  /** Every image turned out unreadable: nothing to bracket, only unreadable labels to write. */
  | 'all-unreadable'
  | 'empty';

export interface DichotomousConflict {
  index: number;
  expected: ToolState;
  given: ToolState;
}

export interface DichotomousState {
  version: 2;
  /**
   * capturedAt-sorted keys of the images still in play — this tool's slice minus `unreadable`;
   * every index below indexes into this.
   */
  keys: string[];
  /** Keys excluded because the image cannot be judged, capturedAt-sorted at init then appended. */
  unreadable: string[];
  lo: number;
  hi: number;
  phase: DichotomousPhase;
  answers: Record<number, ToolState>;
  verifyQueue: number[];
  verifyTotal: number;
  conflict: DichotomousConflict | null;
  /** Incremented once per OK/Worn answer, endpoint confirmations included. */
  comparisonCount: number;
  /** Upper bound on the *search* steps only — a full run costs at most this + 2 answers. */
  maxComparisons: number;
}

// ---------- sorting ----------

/**
 * Ascending by real capture time, stable tie-break on `key`.
 *
 * Used on load by LabelingProvider for every view. The search re-sorts its own working set as
 * well: a binary search depends on this order, so it never trusts the order it was handed
 * (the backend listed images by `imageKey` text before 2026-09-23, operation "10" before "2").
 */
export function sortByCapturedAt(items: ToolImage[]): ToolImage[] {
  return [...items].sort((a, b) => {
    const timeA = Date.parse(a.capturedAt);
    const timeB = Date.parse(b.capturedAt);
    const bothParsed = !Number.isNaN(timeA) && !Number.isNaN(timeB);
    // Unparseable timestamps (should not happen) degrade to a lexicographic comparison
    // rather than producing an arbitrary NaN-driven order.
    const delta = bothParsed
      ? timeA - timeB
      : a.capturedAt < b.capturedAt
        ? -1
        : a.capturedAt > b.capturedAt
          ? 1
          : 0;
    if (delta !== 0) return delta;
    return a.key < b.key ? -1 : a.key > b.key ? 1 : 0;
  });
}

// ---------- init ----------

/**
 * Images already labeled `unreadable` never enter the search — asking about them again would be
 * pointless and the bulk write would overwrite what the annotator recorded. `n` below counts the
 * remaining, readable-as-far-as-we-know images.
 *
 * Phase chosen at init:
 * - `n === 0` → `'empty'` (nothing to label), or `'all-unreadable'` when the slice only holds
 *   images already known to be unreadable.
 * - `n === 1` → `'endpoint-lo'`, with `lo === hi === 0`. A single image cannot contain a
 *   transition, so the one endpoint question resolves the whole slice into `'all-ok'` or
 *   `'all-worn'` — both of which `computeBulkPlan` (task 3) already handles. This is the only
 *   state where the `lo < hi` invariant does not hold; it is never a `'search'` state, so
 *   nothing downstream computes a midpoint from it.
 * - `n >= 2` → `'endpoint-lo'` with `lo = 0`, `hi = n - 1`.
 */
export function initDichotomousSearch(items: ToolImage[]): DichotomousState {
  const sorted = sortByCapturedAt(items);
  const keys = sorted.filter((item) => item.label !== 'unreadable').map((item) => item.key);
  const unreadable = sorted.filter((item) => item.label === 'unreadable').map((item) => item.key);
  const n = keys.length;

  return {
    version: 2,
    keys,
    unreadable,
    lo: 0,
    hi: n > 0 ? n - 1 : 0,
    phase: n > 0 ? 'endpoint-lo' : unreadable.length > 0 ? 'all-unreadable' : 'empty',
    answers: {},
    verifyQueue: [],
    verifyTotal: 0,
    conflict: null,
    comparisonCount: 0,
    maxComparisons: searchBudget(n),
  };
}

/** log2(0) is -Infinity and log2(1) is 0 — clamp so the bound is never negative or NaN. */
function searchBudget(n: number): number {
  return n > 1 ? Math.ceil(Math.log2(n)) : 0;
}

// ---------- current question ----------

/**
 * Index of the image to show the annotator, or `null` when the current phase asks no question.
 * During `'verify'` that is the front of `verifyQueue` (an empty queue asks nothing — such a
 * state should have moved to `'summary'`). `'conflict'` returns `null`: the conflicting image is
 * named by `state.conflict`, and no answer is accepted until the conflict is resolved.
 */
export function currentIndex(state: DichotomousState): number | null {
  switch (state.phase) {
    case 'endpoint-lo':
      return 0;
    case 'endpoint-hi':
      return state.keys.length - 1;
    case 'search':
      return Math.floor((state.lo + state.hi) / 2);
    case 'verify':
      return state.verifyQueue.length > 0 ? state.verifyQueue[0] : null;
    default:
      return null;
  }
}

// ---------- answering ----------

/**
 * Records one answer and returns the next state. Never mutates `state` (the `answers` record is
 * copied too). An `'unreadable'` answer is not a comparison: it takes the image out of the
 * sequence (see `excludeUnreadable`) and the same phase asks its next question.
 *
 * Contract: this function owns the four question phases `'endpoint-lo'`, `'endpoint-hi'`,
 * `'search'` and `'verify'`. Called in any other phase — or in `'verify'` with an empty queue —
 * it **throws**: silently returning the state unchanged would hide a caller bug behind a frozen
 * UI. `rand` is only used when a converged search enters verification, and is injectable so a
 * run can be made reproducible.
 */
export function answerDichotomous(
  state: DichotomousState,
  label: ImageLabel,
  rand: () => number = Math.random
): DichotomousState {
  const index = currentIndex(state);
  if (index === null) {
    throw new Error(`answerDichotomous: no question to answer in phase "${state.phase}"`);
  }

  if (label === 'unreadable') return excludeUnreadable(state, index, rand);

  const next: DichotomousState = {
    ...state,
    answers: { ...state.answers, [index]: label },
    comparisonCount: state.comparisonCount + 1,
  };

  switch (state.phase) {
    case 'endpoint-lo':
      // First image already worn → nothing to search for, the whole slice is presumably worn.
      // For n === 1 the single image *is* the whole slice, so the same branch resolves it.
      if (label === 'worn') return { ...next, phase: 'all-worn' };
      if (state.keys.length === 1) return { ...next, phase: 'all-ok' };
      return { ...next, phase: 'endpoint-hi' };

    case 'endpoint-hi':
      // Last image still ok → the tool never shows wear in this data (symmetric degenerate case).
      if (label === 'ok') return { ...next, phase: 'all-ok' };
      // Both endpoints confirmed (0 = ok, n-1 = worn). With n === 2 the bracket is already
      // converged — there is no index strictly between 0 and 1 to probe — so it skips `'search'`.
      return converged(state.lo, state.hi)
        ? beginVerification(next, rand)
        : { ...next, phase: 'search' };

    case 'search': {
      const lo = label === 'ok' ? index : state.lo;
      const hi = label === 'worn' ? index : state.hi;
      const narrowed = { ...next, lo, hi };
      return converged(lo, hi)
        ? beginVerification(narrowed, rand)
        : { ...narrowed, phase: 'search' };
    }

    case 'verify': {
      // The whole point of step 3: an answer that contradicts its zone stops everything. Recording
      // it first keeps `answers` the single source of truth for task 3's bound recomputation.
      const expected = expectedLabel(state, index);
      if (label !== expected) {
        return { ...next, phase: 'conflict', conflict: { index, expected, given: label } };
      }
      // Match → drop the verified image from the front of the queue. `verifyTotal` stays put so
      // the progress line keeps counting against the sample size it started with.
      const verifyQueue = state.verifyQueue.slice(1);
      return { ...next, verifyQueue, phase: verifyQueue.length === 0 ? 'summary' : 'verify' };
    }

    default:
      throw new Error(`answerDichotomous: phase "${state.phase}" is not handled by this module`);
  }
}

/**
 * Takes the image at `index` — the question on screen, never an answered one — out of the
 * sequence, then lets the phase that asked carry on with what is left.
 *
 * Every index above the removed one shifts down by one. `hi` also moves when it *is* the removed
 * index (the last-image endpoint); `lo` never has to, because when the first image goes the next
 * one takes over index `0`. The OK/Worn answers, the bracket and its adjacency therefore survive
 * unchanged, which is what keeps `computeBulkPlan`'s partition exact.
 *
 * - `'endpoint-lo'` / `'endpoint-hi'`: the new first / last image is asked instead. A last-image
 *   endpoint left with the single, already-OK first image resolves to `'all-ok'`.
 * - `'search'`: the bracket shrank by one; it may have converged.
 * - `'verify'`: a replacement is drawn (same zone first) so unreadable images do not silently
 *   weaken the check; without one the sample simply shrinks.
 * - No image left at all → `'all-unreadable'`.
 */
function excludeUnreadable(
  state: DichotomousState,
  index: number,
  rand: () => number
): DichotomousState {
  const shift = (position: number): number => (position > index ? position - 1 : position);
  const keys = state.keys.filter((_, position) => position !== index);
  const next: DichotomousState = {
    ...state,
    keys,
    unreadable: [...state.unreadable, state.keys[index]],
    answers: Object.fromEntries(
      Object.entries(state.answers).map(([position, label]) => [shift(Number(position)), label])
    ),
    lo: shift(state.lo),
    hi: Math.max(0, state.hi >= index ? state.hi - 1 : state.hi),
    maxComparisons: searchBudget(keys.length),
  };

  if (keys.length === 0) return { ...next, phase: 'all-unreadable' };

  switch (state.phase) {
    case 'endpoint-lo':
      return next;

    case 'endpoint-hi':
      return keys.length === 1 ? { ...next, phase: 'all-ok' } : next;

    case 'search':
      return converged(next.lo, next.hi) ? beginVerification(next, rand) : next;

    case 'verify': {
      const remaining = state.verifyQueue.slice(1).map(shift);
      // `index` is never `lo` or `hi` themselves (both are answered), so the comparison is strict.
      const zone: ToolState = index < state.lo ? 'ok' : 'worn';
      const replacement = drawVerificationReplacement(
        { ...next, verifyQueue: remaining },
        zone,
        rand
      );
      const verifyQueue =
        replacement === null ? remaining : [...remaining, replacement].sort((a, b) => a - b);
      return {
        ...next,
        verifyQueue,
        verifyTotal: replacement === null ? state.verifyTotal - 1 : state.verifyTotal,
        phase: verifyQueue.length === 0 ? 'summary' : 'verify',
      };
    }

    default:
      throw new Error(`answerDichotomous: phase "${state.phase}" is not handled by this module`);
  }
}

/**
 * Converged: `lo` is the last OK image and `hi` the first Worn one, with nothing in between.
 * Both call sites hand the converged state to `beginVerification`, which decides between
 * `'verify'` and `'summary'` — so the n === 2 path and the narrowing path can never drift apart.
 */
function converged(lo: number, hi: number): boolean {
  return hi - lo === 1;
}
